import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/custom/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Image as ImageIcon, Eye, EyeOff, ArrowUp, ArrowDown } from 'lucide-react';

export function AdminBannersPage() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    cta_text: 'Shop Now',
    cta_link: '',
    badge: '',
    image_url: '',
    order: 0,
    active: true,
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/banners/`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      setBanners(data || []);
    } catch (error) {
      console.error('Failed to fetch banners:', error);
      toast.error('Failed to fetch banners');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      // Get upload URL from Cloudflare
      const uploadUrlResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cloudflare/upload-url`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
          },
        }
      );
      
      if (!uploadUrlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadURL, result } = await uploadUrlResponse.json();

      // Upload to Cloudflare
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(uploadURL, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image');
      }

      const uploadResult = await uploadResponse.json();
      const imageUrl = uploadResult.result.variants[0]; // Get public URL

      setFormData(prev => ({ ...prev, image_url: imageUrl }));
      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title,
      subtitle: banner.subtitle || '',
      cta_text: banner.cta_text,
      cta_link: banner.cta_link || '',
      badge: banner.badge || '',
      image_url: banner.image_url,
      order: banner.order,
      active: banner.active,
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/banners/${id}`, {
        method: 'DELETE',
      });
      toast.success('Banner deleted successfully');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggle = async (id) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/banners/${id}/toggle`, {
        method: 'POST',
      });
      toast.success('Banner status updated');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to toggle banner');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = editingBanner
        ? `${process.env.REACT_APP_BACKEND_URL}/api/banners/${editingBanner.id}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/banners/`;

      const method = editingBanner ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      toast.success(`Banner ${editingBanner ? 'updated' : 'created'} successfully`);
      setShowDialog(false);
      setEditingBanner(null);
      setFormData({
        title: '',
        subtitle: '',
        cta_text: 'Shop Now',
        cta_link: '',
        badge: '',
        image_url: '',
        order: 0,
        active: true,
      });
      fetchBanners();
    } catch (error) {
      toast.error('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const moveUp = async (banner, index) => {
    if (index === 0) return;
    const newBanners = [...banners];
    [newBanners[index - 1], newBanners[index]] = [newBanners[index], newBanners[index - 1]];
    await reorderBanners(newBanners);
  };

  const moveDown = async (banner, index) => {
    if (index === banners.length - 1) return;
    const newBanners = [...banners];
    [newBanners[index + 1], newBanners[index]] = [newBanners[index], newBanners[index + 1]];
    await reorderBanners(newBanners);
  };

  const reorderBanners = async (orderedBanners) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/banners/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ banner_ids: orderedBanners.map(b => b.id) }),
      });
      fetchBanners();
    } catch (error) {
      toast.error('Failed to reorder banners');
    }
  };

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold" data-testid="admin-banners-title">Hero Banners</h1>
          <p className="text-sm text-muted-foreground">Manage homepage carousel banners</p>
        </div>
        <Button
          onClick={() => {
            setEditingBanner(null);
            setFormData({
              title: '',
              subtitle: '',
              cta_text: 'Shop Now',
              cta_link: '',
              badge: '',
              image_url: '',
              order: banners.length,
              active: true,
            });
            setShowDialog(true);
          }}
          data-testid="add-banner-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Banner
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">Loading banners...</div>
      ) : (
        <div className="grid gap-4">
          {banners.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <ImageIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">No banners created yet</p>
                <Button onClick={() => setShowDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Banner
                </Button>
              </CardContent>
            </Card>
          ) : (
            banners.map((banner, index) => (
              <Card key={banner.id} className="overflow-hidden" data-testid="banner-card">
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Banner Preview */}
                  <div className="md:w-1/3 relative aspect-[2.5/1] md:aspect-auto">
                    {banner.image_url ? (
                      <img
                        src={banner.image_url}
                        alt={banner.title}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-muted flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-muted-foreground" />
                      </div>
                    )}
                    {banner.badge && (
                      <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground">
                        {banner.badge}
                      </Badge>
                    )}
                    {!banner.active && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Badge variant="secondary">Inactive</Badge>
                      </div>
                    )}
                  </div>

                  {/* Banner Details */}
                  <div className="flex-1 p-4 md:p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold mb-1">{banner.title}</h3>
                        {banner.subtitle && (
                          <p className="text-sm text-muted-foreground mb-2">{banner.subtitle}</p>
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            Order: {banner.order}
                          </Badge>
                          <Badge variant="outline" className="text-xs">
                            CTA: {banner.cta_text}
                          </Badge>
                          {banner.cta_link && (
                            <Badge variant="outline" className="text-xs">
                              Link: {banner.cta_link}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveUp(banner, index)}
                          disabled={index === 0}
                          data-testid="move-up-button"
                        >
                          <ArrowUp className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => moveDown(banner, index)}
                          disabled={index === banners.length - 1}
                          data-testid="move-down-button"
                        >
                          <ArrowDown className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggle(banner.id)}
                        data-testid="toggle-banner-button"
                      >
                        {banner.active ? (
                          <>
                            <Eye className="w-4 h-4 mr-1" />
                            Active
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-4 h-4 mr-1" />
                            Inactive
                          </>
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(banner)}
                        data-testid="edit-banner-button"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(banner.id)}
                        data-testid="delete-banner-button"
                      >
                        <Trash2 className="w-4 h-4 mr-1 text-destructive" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="dialog-title">
              {editingBanner ? 'Edit Banner' : 'Add New Banner'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Welcome to Mothering"
                required
                data-testid="banner-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subtitle">Subtitle</Label>
              <Input
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                placeholder="Quality products for your little ones"
                data-testid="banner-subtitle-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cta_text">CTA Button Text *</Label>
                <Input
                  id="cta_text"
                  value={formData.cta_text}
                  onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                  placeholder="Shop Now"
                  required
                  data-testid="banner-cta-text-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cta_link">CTA Link</Label>
                <Input
                  id="cta_link"
                  value={formData.cta_link}
                  onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                  placeholder="/products or /categories/toys"
                  data-testid="banner-cta-link-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="badge">Badge Text</Label>
              <Input
                id="badge"
                value={formData.badge}
                onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                placeholder="Free Delivery, New, 50% OFF"
                data-testid="banner-badge-input"
              />
              <p className="text-xs text-muted-foreground">Optional badge shown on banner</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_url">Banner Image *</Label>
              <div className="flex gap-2">
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="Or paste image URL manually"
                  data-testid="banner-image-url-input"
                />
                <div className="relative">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    disabled={uploading}
                  />
                  <Button type="button" variant="outline" disabled={uploading}>
                    {uploading ? 'Uploading...' : 'Upload'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Recommended: 1920x800px or wider. Use aspect ratio 2.5:1 for best results
              </p>
              {formData.image_url && (
                <div className="mt-2 aspect-[2.5/1] overflow-hidden rounded-md border">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="order">Display Order</Label>
                <Input
                  id="order"
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  placeholder="0"
                  data-testid="banner-order-input"
                />
                <p className="text-xs text-muted-foreground">Lower number = higher priority</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="active">Status</Label>
                <div className="flex items-center space-x-2 pt-2">
                  <Switch
                    id="active"
                    checked={formData.active}
                    onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                    data-testid="banner-active-switch"
                  />
                  <Label htmlFor="active" className="font-normal cursor-pointer">
                    {formData.active ? 'Active' : 'Inactive'}
                  </Label>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDialog(false)}
                disabled={saving}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-banner-button">
                {saving ? 'Saving...' : editingBanner ? 'Update Banner' : 'Create Banner'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

export default AdminBannersPage;
